import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag, Tooltip } from 'antd';
import { ClockCircleOutlined, EditOutlined, ExportOutlined, FileExcelOutlined, QrcodeOutlined } from '@ant-design/icons';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import ColumnTitle from './ColumnTitle';
import TabTable from './TabTable';
import InterestTag from './InterestTag';
import { CATEGORIES, CATEGORY_COLORS, STATUSES, STATUS_COLORS, date, errorText, money, saveBlob } from './format';
import { paramsOf, withoutPaging } from './listParams';

/**
 * The returns of a company, a page at a time from the server, with what is
 * paid; overdue unpaid ones first and red. The cards above count everything
 * the filters match, and a click on one filters by it.
 */
export default function ReturnsPanel({ company, canManage, hasStatements, projects, selected, onSelect, onAb, onOpen, onSlip }) {
  const { t } = useTranslation('taxReturns');
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const params = useMemo(() => paramsOf('returns', queryParams), [queryParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax-returns', company, params],
    queryFn: () => API.taxReturns.getTaxReturns({ company, ...params }),
    enabled: Boolean(company),
    placeholderData: (prev) => prev,
  });
  const items = data?.items || [];
  const summary = data?.summary;
  const dueSoonCount = data?.dueSoonCount || 0;
  const dueSoon = ['true', true].includes(queryParams.dueSoon);
  // Picked by filing date, returns without one are in the list by their period; say how many.
  const byFiledDate = queryParams.monthBy !== 'due' && Boolean(queryParams.year || queryParams.month);
  const withoutFiledAtCount = data?.withoutFiledAtCount || 0;

  const statusLabel = (s) => t(`status.${s}`, { context: canManage ? undefined : 'client' });
  const dueInText = (days) => (days === 0 ? t('dueSoon.today') : t('dueSoon.inDays', { count: days }));
  const toggleStatus = (s) => setQueryParams({ status: queryParams.status === s ? undefined : s, dueSoon: undefined, offset: 1 });
  const toggleDueSoon = () => setQueryParams({ dueSoon: dueSoon ? undefined : true, status: undefined, offset: 1 });

  const exportMutation = useMutation({
    mutationFn: () => {
      const filters = withoutPaging(params);
      const labels = {
        sheet: t('export.sheet'),
        summarySheet: t('export.summarySheet'),
        project: t('fields.project'),
        title: t('fields.title'),
        category: t('fields.category'),
        type: t('fields.type'),
        period: t('fields.purpose'),
        bop: t('fields.bop'),
        filedAt: t('fields.filedAt'),
        noFiledAt: t('noFiledAt.tag'),
        dueDate: t('fields.dueDate'),
        amount: t('fields.amount'),
        paidAmount: t('fields.paidAmount'),
        lastPaidAt: t('fields.lastPaidAt'),
        status: t('fields.status'),
        interest: t('interest.column'),
        interestState: t('interest.stateColumn'),
        interest_paid: t('interest.paid'),
        interest_unpaid: t('interest.unpaid'),
        interest_estimated: t('interest.estimated'),
        interest_manual: t('interest.manual'),
        note: t('fields.note'),
        count: t('summary.count'),
        open: t('summary.open'),
        overdueCount: t('summary.overdue'),
        dueSoonCount: t('dueSoon.card'),
        noProject: t('filters.noProject'),
        total: t('summary.total'),
        ...Object.fromEntries(STATUSES.map((s) => [`status_${s}`, statusLabel(s)])),
        ...Object.fromEntries(CATEGORIES.map((c) => [`category_${c}`, t(`category.${c}`)])),
      };
      return API.taxReturns.exportTaxReturns(company, filters, labels);
    },
    onSuccess: (blob) => saveBlob(blob, `${t('export.fileName')}.xlsx`),
  });

  const columns = [
    {
      title: () => <ColumnTitle title={t('fields.project')} sort="project" />,
      dataIndex: 'project',
      width: 150,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
    {
      title: () => <ColumnTitle title={t('fields.title')} sort="title" search />,
      dataIndex: 'title',
      render: (v, r) => (
        <div>
          <div>{v || r.fileName}</div>
          <div className="text-xs opacity-60">
            {r.category && (
              <Tag color={CATEGORY_COLORS[r.category]} style={{ marginRight: 6 }}>
                {t(`category.${r.category}`)}
              </Tag>
            )}
            {r.bop || t('drawer.noBop')}
            {r.abExport && (
              <Tooltip title={t('ab.exportedTooltip', { date: date(r.abExport.at), by: r.abExport.byName || '—' })}>
                <Tag color="purple" style={{ marginLeft: 6 }}>
                  {t('ab.exportedTag')}
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    { title: t('fields.type'), dataIndex: 'type', width: 80 },
    {
      title: () => <ColumnTitle title={t('fields.purpose')} sort="purpose" />,
      dataIndex: 'purpose',
      width: 120,
      // PDPO returns carry no PID; their period is the month the income was paid.
      render: (v, r) => v || (r.period?.month ? `${r.period.month}/${r.period.year}` : '—'),
    },
    {
      title: () => <ColumnTitle title={t('fields.filedAt')} sort="filedAt" />,
      dataIndex: 'filedAt',
      width: 120,
      // No filing date on record: said so, never replaced by another date.
      render: (v, r) =>
        r.filedAtMissing ? (
          <Tooltip title={t('noFiledAt.tooltip')}>
            <Tag color="gold" className="whitespace-normal">
              {t('noFiledAt.tag')}
            </Tag>
          </Tooltip>
        ) : (
          date(v)
        ),
    },
    {
      title: () => <ColumnTitle title={t('fields.dueDate')} sort="dueDate" />,
      dataIndex: 'dueDate',
      width: 130,
      render: (v, r) => (
        <div>
          <div>{date(v)}</div>
          {r.dueInDays !== null && r.dueInDays !== undefined && (
            <Tag color="orange" icon={<ClockCircleOutlined />} className="tax-return-due-tag">
              {dueInText(r.dueInDays)}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: () => <ColumnTitle title={t('fields.amount')} sort="amount" />,
      dataIndex: 'amount',
      width: 150,
      align: 'right',
      render: money,
    },
    hasStatements && {
      title: () => <ColumnTitle title={t('fields.paidAmount')} sort="paidAmount" />,
      dataIndex: 'paidAmount',
      width: 150,
      align: 'right',
      render: money,
    },
    hasStatements && {
      title: t('fields.status'),
      dataIndex: 'status',
      width: 150,
      render: (s, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLORS[s]}>{statusLabel(s)}</Tag>
          {r.override && (
            <Tooltip title={r.override.reason}>
              <EditOutlined />
            </Tooltip>
          )}
        </Space>
      ),
    },
    hasStatements && {
      title: t('interest.column'),
      dataIndex: 'lateInterest',
      width: 190,
      render: (li) => <InterestTag value={li} />,
    },
    {
      title: t('slip.column'),
      dataIndex: 'paymentSlip',
      // Wide enough for the longest label ("Payment slip", "Fletëpagesa") with its icon.
      width: 150,
      align: 'center',
      render: (slip, r) =>
        slip || r.interestSlip ? (
          <Space direction="vertical" size={4}>
            {slip && (
              <Button
                shape="round"
                size="small"
                icon={<QrcodeOutlined />}
                onClick={(e) => {
                  // The row opens the details; this button only opens the slip.
                  e.stopPropagation();
                  onSlip(r);
                }}
              >
                {t('slip.button')}
              </Button>
            )}
            {r.interestSlip && (
              <Button
                shape="round"
                size="small"
                danger
                icon={<QrcodeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSlip({ ...r, paymentSlip: r.interestSlip, slipKind: 'interest' });
                }}
              >
                {t('interest.slipButton')}
              </Button>
            )}
          </Space>
        ) : null,
    },
  ].filter(Boolean);

  const statusCards = STATUSES.map((s) => ({ status: s, count: data?.statusCounts?.[s] || 0 })).filter(
    // Without statements only the returns still waiting for their data are worth a card.
    (c) => hasStatements || (c.status === 'pending_entry' && c.count > 0)
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {dueSoonCount > 0 && !dueSoon && (
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined className="tax-return-due-pulse" />}
          message={t(canManage ? 'dueSoon.alert' : 'dueSoon.alert_client', { count: dueSoonCount, days: data.dueSoonDays })}
          action={
            <Button shape="round" size="small" onClick={toggleDueSoon}>
              {t('dueSoon.show')}
            </Button>
          }
        />
      )}

      <Space wrap>
        {dueSoonCount > 0 && (
          <Card size="small" hoverable onClick={toggleDueSoon} style={{ minWidth: 130, borderColor: dueSoon ? '#1677ff' : undefined }}>
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              {t('dueSoon.card')}
            </Tag>
            <div className="mt-1 text-xl font-semibold">{dueSoonCount}</div>
          </Card>
        )}
        {statusCards.map(({ status, count }) => (
          <Card
            key={status}
            size="small"
            hoverable
            onClick={() => toggleStatus(status)}
            style={{ minWidth: 130, borderColor: queryParams.status === status ? '#1677ff' : undefined }}
          >
            <Tag color={STATUS_COLORS[status]}>{statusLabel(status)}</Tag>
            <div className="mt-1 text-xl font-semibold">{count}</div>
          </Card>
        ))}
        {summary && (
          // Both totals side by side, as tall as the status cards; what is still to pay stands out in red.
          <Card size="small">
            <div className="flex items-stretch gap-6">
              <div>
                <div className="text-xs opacity-70">{t('summary.amount')}</div>
                <div className="mt-1 whitespace-nowrap text-xl font-semibold">
                  {money(summary.totals.amount)} <span className="text-xs font-normal opacity-60">RSD</span>
                </div>
              </div>
              <div className="border-l border-gray-200 pl-6">
                <div className="text-xs opacity-70">{t('summary.open')}</div>
                <div
                  className="mt-1 whitespace-nowrap text-xl font-semibold"
                  style={{ color: summary.totals.open > 0 ? '#cf1322' : '#389e0d' }}
                >
                  {money(summary.totals.open)} <span className="text-xs font-normal opacity-60">RSD</span>
                </div>
              </div>
            </div>
          </Card>
        )}
      </Space>

      {(isError || exportMutation.isError) && <Alert type="error" showIcon message={errorText(t, error || exportMutation.error)} />}

      {byFiledDate && withoutFiledAtCount > 0 && (
        <Alert type="info" showIcon message={t('noFiledAt.alert', { count: withoutFiledAtCount, column: t('fields.filedAt') })} />
      )}

      <TabTable
        tab="returns"
        title={t('tabs.returns')}
        data={items}
        total={data?.count || 0}
        status={isLoading ? 'pending' : isError ? 'error' : 'success'}
        columns={columns}
        // Fixed widths with the return's name taking what is left (and wrapping),
        // so on a normal screen every column fits; only a narrow one scrolls sideways.
        tableLayout="fixed"
        scroll={{ x: 1340 }}
        projects={projects}
        showStatus={hasStatements}
        actions={
          <>
            {canManage && (
              <Tooltip title={selected.length ? null : t('ab.selectHint')}>
                <Button
                  shape="round"
                  // Antd greys a disabled button to near-transparent on the green bar; keep it readable.
                  className={selected.length ? 'h-8' : 'h-8 !border-transparent !bg-white !text-zinc-400'}
                  icon={<ExportOutlined />}
                  disabled={!selected.length}
                  onClick={onAb}
                >
                  {t('ab.button', { count: selected.length })}
                </Button>
              </Tooltip>
            )}
            <Button
              shape="round"
              className="h-8"
              icon={<FileExcelOutlined />}
              loading={exportMutation.isPending}
              onClick={() => exportMutation.mutate()}
            >
              {t('export.button')}
            </Button>
          </>
        }
        rowClassName={(r) => (r.overdue ? 'tax-return-overdue' : r.dueInDays !== null ? 'tax-return-due-soon' : '')}
        rowSelection={
          canManage
            ? {
                selectedRowKeys: selected.map((r) => r._id),
                // The picked rows stay picked across pages.
                onChange: (keys, rows) => {
                  const others = selected.filter((r) => !items.some((i) => i._id === r._id));
                  onSelect([...others, ...rows.filter(Boolean)]);
                },
                // Only PDPO returns with a BOP can be booked: the BOP closes the liability when the tax is paid.
                getCheckboxProps: (r) => {
                  const off = r.type !== 'PDPO' || !r.bop;
                  // Hovering a greyed box says why it cannot be picked.
                  return { disabled: off, title: off ? t('ab.onlyPdpo') : undefined };
                },
              }
            : undefined
        }
        onRow={(r) => ({ onClick: () => onOpen(r._id), style: { cursor: 'pointer' } })}
      />

      {summary?.byProject?.length > 0 && (
        <Card size="small" title={t('summary.byProject')}>
          <Table
            size="small"
            rowKey={(g) => g.project?._id || 'none'}
            pagination={false}
            dataSource={summary.byProject}
            columns={[
              {
                title: t('fields.project'),
                dataIndex: 'project',
                render: (p) => (p ? `${p.code} ${p.name}` : t('filters.noProject')),
              },
              { title: t('summary.count'), dataIndex: 'count', align: 'right', width: 90 },
              { title: t('summary.amount'), dataIndex: 'amount', align: 'right', render: money },
              hasStatements && { title: t('fields.paidAmount'), dataIndex: 'paid', align: 'right', render: money },
              { title: t('summary.open'), dataIndex: 'open', align: 'right', render: money },
              hasStatements && {
                title: t('summary.overdue'),
                dataIndex: 'overdue',
                align: 'right',
                width: 110,
                render: (v) => (v ? <Tag color="red">{v}</Tag> : 0),
              },
              {
                title: t('dueSoon.card'),
                dataIndex: 'dueSoon',
                align: 'right',
                width: 120,
                render: (v) => (v ? <Tag color="orange">{v}</Tag> : 0),
              },
              hasStatements && {
                title: t('fields.status'),
                dataIndex: 'byStatus',
                render: (b) => (
                  <Space size={2} wrap>
                    {STATUSES.filter((s) => b[s]).map((s) => (
                      <Tag key={s} color={STATUS_COLORS[s]}>
                        {statusLabel(s)}: {b[s]}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
            ].filter(Boolean)}
          />
        </Card>
      )}
    </Space>
  );
}
